import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { hybridApiBaseUrl, resolveMessagingApiBaseUrl } from './hybridFlags';
import { isMessagingAccessBlocked } from './messagingAccess';

const baseURL = hybridApiBaseUrl;
const fallbackBaseURL = 'https://api.gamestar.co.il';
const isPrivateNetworkBaseUrl = (url: string) =>
  /^http:\/\/(localhost|127\.0\.0\.1|10\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.)/i.test(url);
const isHttpBaseUrl = (url: string) => /^http:\/\//i.test(url);
const isHttpsHostedFrontend = (() => {
  try {
    if (typeof window === 'undefined') return false;
    return String(window.location.protocol || '').toLowerCase() === 'https:';
  } catch {
    return false;
  }
})();
const isPublicHostedFrontend = (() => {
  try {
    if (typeof window === 'undefined') return false;
    const host = String(window.location.hostname || '').trim().toLowerCase();
    if (!host) return false;
    if (host === 'localhost' || host === '127.0.0.1') return false;
    if (/^10\./.test(host)) return false;
    if (/^192\.168\./.test(host)) return false;
    if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(host)) return false;
    return true;
  } catch {
    return false;
  }
})();
// https page + http API base גורם לחסימות בדפדפן (Mixed Content / Network Error).
// במקרה כזה כופים שימוש ב-API המאובטח בענן כ-primary ומדלגים על fallback ל-http.
const shouldForceCloudPrimaryForHttpsPage =
  Boolean(baseURL) &&
  baseURL !== fallbackBaseURL &&
  isHttpsHostedFrontend &&
  isHttpBaseUrl(baseURL);
const shouldPreferCloudFallbackAsPrimary =
  shouldForceCloudPrimaryForHttpsPage ||
  (Boolean(baseURL) &&
    baseURL !== fallbackBaseURL &&
    isPrivateNetworkBaseUrl(baseURL) &&
    isPublicHostedFrontend);
const canUseFallbackBaseUrl =
  !shouldForceCloudPrimaryForHttpsPage &&
  Boolean(baseURL) &&
  baseURL !== fallbackBaseURL &&
  (isPrivateNetworkBaseUrl(baseURL) || shouldPreferCloudFallbackAsPrimary);

if (!baseURL) {
  // eslint-disable-next-line no-console
  console.warn('Hybrid API base URL is not configured');
}

/** LAN/NAS — timeout קצר יותר כדי לעבור מהר ל-fallback בענן כשה-NAS איטי אחרי הפסקת חשמל */
const LAN_PRIMARY_TIMEOUT_MS = 8000;
const CLOUD_TIMEOUT_MS = 30000;
const primaryTimeoutMs =
  baseURL && isPrivateNetworkBaseUrl(baseURL) ? LAN_PRIMARY_TIMEOUT_MS : CLOUD_TIMEOUT_MS;

const hybridApiClient = axios.create({
  baseURL,
  timeout: primaryTimeoutMs,
});
const fallbackHybridApiClient = axios.create({
  baseURL: fallbackBaseURL,
  timeout: CLOUD_TIMEOUT_MS,
});
// Prefer the configured base URL (usually local/LAN) and use cloud only as fallback.
const primaryApiClient = shouldPreferCloudFallbackAsPrimary ? fallbackHybridApiClient : hybridApiClient;
const secondaryApiClient = canUseFallbackBaseUrl
  ? (shouldPreferCloudFallbackAsPrimary ? hybridApiClient : fallbackHybridApiClient)
  : hybridApiClient;
const loggedHybridErrors = new Map<string, number>();
const HYBRID_ERROR_LOG_DEDUP_MS = 15000;
const PRIMARY_UNAVAILABLE_COOLDOWN_MS = 60000;
let primaryUnavailableUntilMs = 0;

/** האם ה-LAN/NAS הוא ה-primary (במצב הזה כדאי לבדוק החלמה מהירה אחרי restart). */
const lanPrimaryIsHybridClient = !shouldPreferCloudFallbackAsPrimary;
/** בדיקת health מהירה ל-primary כדי לנקות cooldown מיד כשה-NAS חוזר (במקום להמתין 60ש'). */
const PRIMARY_PROBE_THROTTLE_MS = 3000;
const PRIMARY_PROBE_TIMEOUT_MS = 2500;
let lastPrimaryProbeAtMs = 0;
let primaryProbeInFlight: Promise<boolean> | null = null;

async function probePrimaryHealthy(): Promise<boolean> {
  if (primaryProbeInFlight) return primaryProbeInFlight;
  primaryProbeInFlight = (async () => {
    try {
      await hybridApiClient.get('/health', { timeout: PRIMARY_PROBE_TIMEOUT_MS });
      return true;
    } catch {
      return false;
    } finally {
      lastPrimaryProbeAtMs = Date.now();
      primaryProbeInFlight = null;
    }
  })();
  return primaryProbeInFlight;
}

if (typeof window !== 'undefined') {
  // חזרה לרשת — לתת ל-primary הזדמנות מיידית במקום להישאר על ה-fallback.
  window.addEventListener('online', () => {
    primaryUnavailableUntilMs = 0;
  });
}

function shouldLogHybridError(key: string): boolean {
  const now = Date.now();
  const last = loggedHybridErrors.get(key) || 0;
  if (now - last < HYBRID_ERROR_LOG_DEDUP_MS) return false;
  loggedHybridErrors.set(key, now);
  return true;
}

hybridApiClient.interceptors.response.use(
  (res) => {
    // תגובה תקינה מה-LAN (כשהוא ה-primary) — ה-NAS חזר, מנקים cooldown מיד.
    if (lanPrimaryIsHybridClient) {
      primaryUnavailableUntilMs = 0;
    }
    return res;
  },
  (err) => {
    const status = Number(err?.response?.status || 0);
    const url = String(err?.config?.url || '');
    const method = String(err?.config?.method || 'get').toUpperCase();
    const logKey = `${method}:${url}:${status || err?.code || 'unknown'}`;
    if (status === 401) {
      if (shouldLogHybridError(logKey)) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Hybrid API] 401 — הטוקן נדחה או חסר. בדוק אימות משתמש והתאמת Firebase בין האפליקציה לשרת.',
          { baseURL, method, url },
        );
      }
    } else if (status === 403) {
      const isMessagesForbidden =
        isMessagingAccessBlocked() && /^\/api\/messages/i.test(url);
      if (shouldLogHybridError(logKey) && !isMessagesForbidden) {
        // eslint-disable-next-line no-console
        console.error('[Hybrid API] 403 — אין הרשאה.', { baseURL, method, url });
      }
    } else if (status >= 500) {
      const isOptionalKb = url === '/api/knowledge-base' || url.startsWith('/api/knowledge-base/');
      if (shouldLogHybridError(logKey) && !isOptionalKb) {
        // eslint-disable-next-line no-console
        console.error('[Hybrid API] שגיאת שרת', status, { baseURL, method, url });
      }
    } else if (!err?.response && err?.message === 'Network Error') {
      if (shouldLogHybridError(logKey)) {
        // eslint-disable-next-line no-console
        console.error(
          '[Hybrid API] Network/CORS — הבקשה לא הגיעה לשרת או נחסמה בדפדפן. בדוק: (1) REACT_APP_QNAP_API_BASE_URL (2) בשרת: CORS מאפשר את origin של הדף (למשל http://localhost:3001) (3) שה-API רץ.',
          { baseURL, method, url },
        );
      }
    }
    return Promise.reject(err);
  },
);

async function authHeaders(forceRefresh = false) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    return {};
  }
  const token = await user.getIdToken(forceRefresh);
  return {
    Authorization: `Bearer ${token}`,
  };
}

async function requestWithFallback(
  method: 'get' | 'post' | 'patch' | 'delete',
  url: string,
  dataOrConfig?: any,
  maybeConfig?: any,
) {
  const data = method === 'post' || method === 'patch' ? dataOrConfig : undefined;
  const config = method === 'post' || method === 'patch' ? maybeConfig : dataOrConfig;
  const buildRequestConfig = async (forceRefresh = false) => {
    const auth = await authHeaders(forceRefresh);
    return {
      ...(config || {}),
      headers: {
        ...((config && config.headers) || {}),
        ...auth,
      },
    };
  };
  const isDashboardStatsRequest = method === 'get' && url === '/api/repairs/dashboard-stats';
  const isDashboardSummaryStateRequest = method === 'get' && url === '/api/repairs/dashboard-summary-state';
  const isWordPressSyncInvoiceRequest = method === 'post' && url === '/api/public/wordpress-sync-invoice';
  /** הודעות — רק NAS (Postgres מקומי); אין fallback לענן */
  const isMessagesApiRequest = /^\/api\/messages/i.test(url);
  const isMessagesMutatingRequest =
    isMessagesApiRequest && (method === 'post' || method === 'patch' || method === 'delete');
  const MESSAGES_MUTATION_TIMEOUT_MS = 45000;
  const MESSAGES_GET_TIMEOUT_MS = 45000;
  const DASHBOARD_SLOW_GET_TIMEOUT_MS = 120000;
  const WORDPRESS_SYNC_TIMEOUT_MS = 90000;
  /** רשימת קריאות / ספירה — NAS איטי אחרי הפעלה; 8ש׳ גורמים ל-fallback לענן ומסך ריק */
  const REPAIRS_LIST_GET_TIMEOUT_MS = 90000;
  const CUSTOMERS_CLEANUP_TIMEOUT_MS = 120000;
  const isRepairsListGetRequest = method === 'get' && url === '/api/repairs';
  const isRepairsSystemTotalRequest = method === 'get' && url === '/api/repairs/meta/system-total';
  const isCustomersCleanupDuplicatesRequest =
    method === 'post' && url === '/api/customers/cleanup-empty-phone-duplicates';
  const applyExtendedTimeout = (cfg: any) => {
    let next = cfg || {};
    if (method === 'get' && (isDashboardStatsRequest || isDashboardSummaryStateRequest)) {
      const prev = Number(next?.timeout);
      const extended = Math.max(Number.isFinite(prev) ? prev : 0, DASHBOARD_SLOW_GET_TIMEOUT_MS);
      next = { ...next, timeout: extended || DASHBOARD_SLOW_GET_TIMEOUT_MS };
    }
    if (method === 'get' && (isRepairsListGetRequest || isRepairsSystemTotalRequest)) {
      const prev = Number(next?.timeout);
      const extended = Math.max(Number.isFinite(prev) ? prev : 0, REPAIRS_LIST_GET_TIMEOUT_MS);
      next = { ...next, timeout: extended || REPAIRS_LIST_GET_TIMEOUT_MS };
    }
    if (isCustomersCleanupDuplicatesRequest) {
      const prev = Number(next?.timeout);
      const extended = Math.max(Number.isFinite(prev) ? prev : 0, CUSTOMERS_CLEANUP_TIMEOUT_MS);
      next = { ...next, timeout: extended || CUSTOMERS_CLEANUP_TIMEOUT_MS };
    }
    if (isWordPressSyncInvoiceRequest) {
      const prev = Number(next?.timeout);
      const extended = Math.max(Number.isFinite(prev) ? prev : 0, WORDPRESS_SYNC_TIMEOUT_MS);
      next = { ...next, timeout: extended || WORDPRESS_SYNC_TIMEOUT_MS };
    }
    if (isMessagesApiRequest) {
      const prev = Number(next?.timeout);
      const extended = Math.max(
        Number.isFinite(prev) ? prev : 0,
        method === 'get' ? MESSAGES_GET_TIMEOUT_MS : MESSAGES_MUTATION_TIMEOUT_MS,
      );
      next = {
        ...next,
        timeout: extended || (method === 'get' ? MESSAGES_GET_TIMEOUT_MS : MESSAGES_MUTATION_TIMEOUT_MS),
      };
    }
    return next;
  };
  const withMessagesFallbackTimeout = (cfg: any) => cfg;
  const requestConfig = applyExtendedTimeout(await buildRequestConfig(false));
  const isReportRollupsRequest =
    method === 'get' &&
    (url === '/api/repairs/report-rollups/meta' || url === '/api/repairs/report-rollups/daily');
  const isRepairsPatchRequest = method === 'patch' && /^\/api\/repairs\/[^/]+$/i.test(url);
  /** מחיקת קריאה לפי id — לא כולל נתיבים כמו lab-summary-templates */
  const isRepairsDeleteRequest =
    method === 'delete' &&
    /^\/api\/repairs\/[^/]+$/i.test(url) &&
    url !== '/api/repairs/lab-summary-templates';
  const isRepairsMarkPriorityInvoiceRequest =
    method === 'post' && /^\/api\/repairs\/[^/]+\/mark-priority-invoice$/i.test(url);
  const isRepairsBulkMarkReturnedRequest = method === 'post' && url === '/api/repairs/bulk-mark-returned';
  const isRepairsHybridMutatingRequest =
    isRepairsPatchRequest ||
    isRepairsDeleteRequest ||
    isRepairsMarkPriorityInvoiceRequest ||
    isRepairsBulkMarkReturnedRequest;
  /** עדכון/יצירה/מחיקה של לקוחות חייבים fallback, אחרת timeout ב-LAN מפיל שמירה תקינה. */
  const isCustomersMutatingRequest =
    /^\/api\/customers(?:\/[^/]+)?$/i.test(url) &&
    (method === 'patch' || method === 'post' || method === 'delete');

  const execute = async (client: any, cfg: any) => {
    if (method === 'get') return await client.get(url, cfg);
    if (method === 'delete') return await client.delete(url, cfg);
    if (method === 'post') return await client.post(url, data, cfg);
    return await client.patch(url, data, cfg);
  };

  const canTemporarilyBypassPrimary =
    secondaryApiClient !== primaryApiClient &&
    Date.now() < primaryUnavailableUntilMs &&
    (method === 'get' || isRepairsHybridMutatingRequest || isCustomersMutatingRequest) &&
    !isMessagesApiRequest &&
    !isDashboardStatsRequest &&
    !isDashboardSummaryStateRequest &&
    !isReportRollupsRequest;

  if (canTemporarilyBypassPrimary) {
    // לפני שמוותרים על ה-LAN למשך כל ה-cooldown — בדיקת health מהירה (מווסתת).
    // אם ה-NAS חזר (למשל אחרי restart) מנקים את ה-cooldown ומשתמשים בו מיד.
    if (lanPrimaryIsHybridClient && Date.now() - lastPrimaryProbeAtMs >= PRIMARY_PROBE_THROTTLE_MS) {
      const healthy = await probePrimaryHealthy();
      if (healthy) {
        primaryUnavailableUntilMs = 0;
      } else {
        return await execute(secondaryApiClient, withMessagesFallbackTimeout(requestConfig));
      }
    } else {
      return await execute(secondaryApiClient, withMessagesFallbackTimeout(requestConfig));
    }
  }

  try {
    return await execute(primaryApiClient, requestConfig);
  } catch (error: any) {
    let requestError = error;
    const status = Number(requestError?.response?.status || 0);
    if (status === 401) {
      try {
        // Retry once with a forced token refresh before fallback logic.
        const refreshedConfig = applyExtendedTimeout(await buildRequestConfig(true));
        return await execute(primaryApiClient, refreshedConfig);
      } catch (retryError: any) {
        const retryStatus = Number(retryError?.response?.status || 0);
        if (retryStatus === 401) {
          throw retryError;
        }
        requestError = retryError;
      }
    }

    const code = String(requestError?.code || '');
    const message = String(requestError?.message || '');
    const isNetworkIssue =
      code === 'ERR_NETWORK' ||
      code === 'ECONNABORTED' ||
      message.includes('Network Error') ||
      message.includes('ERR_CONNECTION_RESET');
    const isServer5xx = status >= 500;
    const primaryIsCloud = primaryApiClient === fallbackHybridApiClient;
    const normalizeApiBase = (value: string) => String(value || '').trim().replace(/\/+$/, '');
    const sameApiHost =
      normalizeApiBase(primaryApiClient.defaults.baseURL || baseURL) ===
      normalizeApiBase(secondaryApiClient.defaults.baseURL || fallbackBaseURL);
    const shouldFallback =
      !sameApiHost &&
      (method === 'get' || isRepairsHybridMutatingRequest || isCustomersMutatingRequest) &&
      !isMessagesApiRequest &&
      !isDashboardStatsRequest &&
      !isDashboardSummaryStateRequest &&
      !isReportRollupsRequest &&
      (isNetworkIssue ||
        (isServer5xx && !primaryIsCloud) ||
        (status === 404 && isRepairsDeleteRequest));

    if (!shouldFallback) throw requestError;
    if (isNetworkIssue) {
      primaryUnavailableUntilMs = Date.now() + PRIMARY_UNAVAILABLE_COOLDOWN_MS;
    }
    // eslint-disable-next-line no-console
    console.warn('[Hybrid API] primary endpoint unavailable, retrying on cloud fallback', {
      primaryBaseURL: primaryApiClient.defaults.baseURL || baseURL,
      fallbackBaseURL,
      method: method.toUpperCase(),
      url,
      status: requestError?.response?.status || null,
      code: requestError?.code || requestError?.message,
    });
    return await execute(secondaryApiClient, withMessagesFallbackTimeout(requestConfig));
  }
}

const hybridApi = {
  get: async (url: string, config?: any) => requestWithFallback('get', url, config),
  post: async (url: string, data?: any, config?: any) => requestWithFallback('post', url, data, config),
  patch: async (url: string, data?: any, config?: any) => requestWithFallback('patch', url, data, config),
  delete: async (url: string, config?: any) => requestWithFallback('delete', url, config),
};

export default hybridApi;

/** בקשות ישירות ל-NAS/LAN בלבד — בלי fallback לענן (למשל בדיקת יכולות API) */
export const lanHybridApi = hybridApiClient;

/** תשובת LAN מינימלית (בלי AxiosResponse — @types/axios הישן מתנגש עם axios 1.x) */
export type LanAuthResponse<T> = { data: T };

/** GET ל-NAS עם טוקן Firebase (axios בפרויקט לא תומך ב-interceptor אסינכרוני) */
export async function lanGetWithAuth<T = unknown>(
  url: string,
  config?: Record<string, unknown>,
): Promise<LanAuthResponse<T>> {
  const auth = await authHeaders(false);
  const cfg = (config || {}) as { headers?: Record<string, string>; params?: Record<string, unknown>; timeout?: number };
  const res = await hybridApiClient.get(url, {
    ...cfg,
    headers: {
      ...(cfg.headers || {}),
      ...auth,
    },
  });
  return { data: res.data as T };
}

/** POST ל-NAS בלבד — הודעות קבוצה חייבות להישמר ב-Postgres המקומי */
export async function lanPostWithAuth<T = unknown>(
  url: string,
  data?: unknown,
  config?: Record<string, unknown>,
): Promise<LanAuthResponse<T>> {
  const auth = await authHeaders(false);
  const cfg = (config || {}) as { headers?: Record<string, string>; timeout?: number };
  const res = await hybridApiClient.post(url, data, {
    ...cfg,
    timeout: Math.max(Number(cfg.timeout) || 0, 45000) || 45000,
    headers: {
      ...(cfg.headers || {}),
      ...auth,
    },
  });
  return { data: res.data as T };
}

const messagingApiClient = axios.create({
  baseURL: resolveMessagingApiBaseUrl(),
  timeout: 45000,
});

/** הודעות — אותו שרת לכל המשתמשים (LAN או https://api.gamestar.co.il) */
export async function messagingGetWithAuth<T = unknown>(
  url: string,
  config?: Record<string, unknown>,
): Promise<LanAuthResponse<T>> {
  const auth = await authHeaders(false);
  const cfg = (config || {}) as { headers?: Record<string, string>; params?: Record<string, unknown>; timeout?: number };
  const res = await messagingApiClient.get(url, {
    ...cfg,
    timeout: Math.max(Number(cfg.timeout) || 0, 45000) || 45000,
    headers: { ...(cfg.headers || {}), ...auth },
  });
  return { data: res.data as T };
}

export async function messagingPostWithAuth<T = unknown>(
  url: string,
  data?: unknown,
  config?: Record<string, unknown>,
): Promise<LanAuthResponse<T>> {
  const auth = await authHeaders(false);
  const cfg = (config || {}) as { headers?: Record<string, string>; timeout?: number };
  const res = await messagingApiClient.post(url, data, {
    ...cfg,
    timeout: Math.max(Number(cfg.timeout) || 0, 45000) || 45000,
    headers: { ...(cfg.headers || {}), ...auth },
  });
  return { data: res.data as T };
}

export async function messagingPatchWithAuth<T = unknown>(
  url: string,
  data?: unknown,
  config?: Record<string, unknown>,
): Promise<LanAuthResponse<T>> {
  const auth = await authHeaders(false);
  const cfg = (config || {}) as { headers?: Record<string, string>; timeout?: number };
  const res = await messagingApiClient.patch(url, data, {
    ...cfg,
    timeout: Math.max(Number(cfg.timeout) || 0, 45000) || 45000,
    headers: { ...(cfg.headers || {}), ...auth },
  });
  return { data: res.data as T };
}
